<template>
  <ModalDefault id="modal_alertas" title="Gestión de Alertas" size="xl" :fnc-on-close="handleClose">
    <template #modal-body>
      <div class="relative w-full min-h-[700px] bg-gray-50/50 dark:bg-gray-900/10 overflow-hidden rounded-xl">

        <!-- MAIN LIST VIEW -->
        <div class="w-full h-full pb-6 pt-2 transition-all duration-500" :class="{ 'opacity-40 pointer-events-none blur-[2px] scale-[0.98]': isDrawerOpen }">
                    <!-- Optimized Single-Row Header & Tabs -->
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 bg-white dark:bg-gray-800/40 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-2 py-1">
                <InformationCircleIcon class="w-5 h-5 text-blue-500" />
                <div>
                  <h3 class="text-sm font-bold text-gray-800 dark:text-gray-100">Gestión de alertas</h3>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ alertGroups.active.length }} activas · {{ alertGroups.inactive.length }} cerradas</p>
                </div>
              </div>
            </div>

            <ButtonDefault
              color="blue"
              class-add="active:scale-95"
              @click="handleNewAlert"
            >
              <PlusIcon class="w-4 h-4 mr-2" />
              Nueva Alerta
            </ButtonDefault>
          </div>

          <!-- Alerts List -->
          <div class="max-h-[580px] overflow-y-auto pr-2 custom-scrollbar">
            <div class="space-y-6">
              <section v-for="section in alertSections" :key="section.key">
                <div class="flex items-center gap-3 mb-3">
                  <h4 class="text-sm font-bold text-gray-700 dark:text-gray-200">{{ section.title }}</h4>
                  <span class="h-px flex-1 bg-gray-200 dark:bg-gray-700"></span>
                </div>
                <TransitionGroup
                  tag="div"
                  enter-active-class="transition-all duration-500 ease-out"
                  enter-from-class="opacity-0 translate-y-4"
                  enter-to-class="opacity-100 translate-y-0"
                  move-class="transition-all duration-500"
                  class="grid grid-cols-1 gap-4"
                >
                <div v-for="(alert, index) in section.alerts" :key="alert.alerId || index">
                <Transition
                  mode="out-in"
                  enter-active-class="transition-all duration-500 ease-out"
                  enter-from-class="opacity-0 scale-95"
                  enter-to-class="opacity-100 scale-100"
                  leave-active-class="transition-all duration-300 ease-in"
                  leave-from-class="opacity-100 scale-100"
                  leave-to-class="opacity-0 scale-95"
                >
                  <!-- NORMAL VIEW OF CARD -->
                  <div
                    v-if="editingId !== alert.alerId"
                    key="view"
                    class="group relative overflow-hidden rounded-xl border transition-all duration-300 hover:shadow-md"
                    :class="[getAlertCardBackground(alert), getAlertCardBorder(alert)]"
                  >
                    <div class="p-4 flex gap-4">
                      <div class="shrink-0 w-10 h-10 rounded-full flex items-center justify-center border shadow-sm" :class="getAlertIconClasses(alert)">
                        <component :is="getAlertIcon(alert)" class="w-5 h-5" />
                      </div>

                      <div class="flex-1 min-w-0">
                        <div class="flex items-center justify-between mb-1">
                          <div class="flex items-center gap-2">
                            <h4 class="text-[10px] font-bold uppercase tracking-widest" :class="getAlertTitleClass(alert)">
                              {{ getAlertTitle(alert) }}
                            </h4>
                            <span v-if="expiredIds.has(alert.alerId as number)" class="px-1.5 py-0.5 rounded text-[8px] font-bold bg-gray-900 text-white uppercase tracking-tighter">Vencida</span>
                          </div>
                          <div class="flex items-center gap-3">
                            <BtnCerrarAlerta
                              v-if="alert.alerEstado === 1 && validatePermiso(['editarAlertaPrivadaPaciente', 'editarAlertaPaciente'])"
                              :alerta="alert"
                              :actions="getAlertActions(alert)"
                              cmot-tipo="CIERRE ALERTA"
                              @closed="handleAlertaCerrada"
                            />
                            <ButtonGroup
                              v-else-if="canManageAlert()"
                              :buttons="getAlertActions(alert)"
                            />
                          </div>
                        </div>

                        <div class="flex items-end justify-between gap-4 mt-2">
                          <div class="flex-1 min-w-0">
                            <p class="text-[13px] font-semibold text-gray-800 dark:text-gray-200 leading-tight">
                              {{ getAlertNombre(alert) }}
                            </p>

                            <div v-if="alert.servicio && alert.tipoAlerta" class="mt-1 flex items-center gap-2 text-[10px] text-gray-500 font-bold uppercase italic">
                              <DocumentTextIcon class="w-3.5 h-3.5" />
                              {{ alert.servicio.coseNombre }}
                            </div>
                          </div>

                          <div class="flex flex-col items-end gap-1 shrink-0 pb-0.5">
                            <div class="flex items-center gap-1.5 text-[10px] text-gray-500 font-medium">
                              <CalendarIcon class="w-3 h-3 text-gray-400" />
                              <span class="uppercase tracking-tighter opacity-70">Creada:</span>
                              {{ formatearFechaEnEspanol(alert.createdAt as string) }}
                            </div>
                            <div class="flex items-center gap-1.5 text-[10px] font-bold" :class="expiredIds.has(alert.alerId as number) ? 'text-red-500' : 'text-gray-500'">
                              <ClockIcon class="w-3 h-3" :class="expiredIds.has(alert.alerId as number) ? 'text-red-400' : 'text-gray-400'" />
                              <span class="uppercase tracking-tighter opacity-70">Vence:</span>
                              {{ alert.alerFecha ? formatearFechaEnEspanol(alert.alerFecha) : 'Sin vencimiento' }}
                            </div>
                            <div v-if="alert.alerEstado !== 1 && alert.alerFechaCierre" class="flex items-center gap-1.5 text-[10px] text-gray-500 font-medium">
                              <XCircleIcon class="w-3 h-3 text-gray-400" />
                              <span class="uppercase tracking-tighter opacity-70">Cerrada:</span>
                              {{ formatearFechaEnEspanol(alert.alerFechaCierre) }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- INLINE EDITING VIEW -->
                  <div
                    v-else
                    key="edit"
                    class="rounded-xl border-2 border-blue-500 bg-white dark:bg-gray-800 p-5 shadow-xl"
                  >
                    <div class="flex items-center justify-between mb-4 border-b border-gray-100 dark:border-gray-700 pb-3">
                      <h4 class="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest flex items-center gap-2">
                        <PencilIcon class="w-4 h-4" />
                        Editando alerta
                      </h4>
                      <button class="text-gray-400 hover:text-gray-600" @click="cancelEdit"><XMarkIcon class="w-5 h-5" /></button>
                    </div>

                    <div class="space-y-4 mb-4">
                      <SelectDefault
                        v-model="formData.alerTablaTipoAlerta"
                        :options="categoriasFiltradas"
                        label="Selecciona una categoría"
                        :map-options="(item: any) => ({ optionText: item.comaNombrelargo, optionValue: item.comaId })"
                        :error="showError('formData.alerTablaTipoAlerta')"
                        @change="handleCategoryChange"
                      />

                      <div v-if="!isTipoRuta">
                        <SelectSearch
                          id="edit_alert_select"
                          v-model:value="formData.alerTipoAlerta"
                          v-model:search="searchAlerta"
                          label="Selecciona un tipo de alerta"
                          :list-group="alertasGrouped || {}"
                          :is-group="true"
                          :readonly="!formData.alerTablaTipoAlerta"
                          :error="showError('formData.alerTipoAlerta')"
                          :options="{ fieldFilter: { name: 'comaNombrelargo', mapOptions: (item: any) => item.comaNombrelargo }, mapOptions: (item: any) => ({ optionText: item.comaNombrelargo, optionValue: item.comaId }) }"
                        />
                      </div>

                      <SelectDefault
                        id="edit_servicio_select"
                        v-model="formData.coseId"
                        label="Selecciona un servicio"
                        :options="serviciosList"
                        :map-options="(item: any) => ({ optionText: item.coseNombre, optionValue: item.coseId })"
                        :readonly="!formData.alerTablaTipoAlerta"
                        :error="showError('formData.coseId')"
                        placeholder="Seleccione un servicio"
                      />

                      <div>
                        <TextareaDefault
                          v-model="formData.alerDetalle"
                          :maxlength="500"
                          label="Descripción de la alerta"
                          :show-character-count="true"
                          placeholder="Escribe los detalles aquí..."
                        />
                      </div>

                      <div class="grid grid-cols-2 gap-4 items-end pt-2">
                        <div class="grid grid-cols-1 gap-1">
                          <DatePickerDefault v-model="formData.alerFecha" label="Fecha de vencimiento" :clearable="true" />
                        </div>
                        <div v-if="validatePermiso(['registrarAlertaPrivadaPaciente'])" class="pb-2 flex flex-col gap-2">
                          <SwitchDefault
                            id="sw-edit-priv"
                            v-model="formData.alerIsPrivada"
                            label="¿Es privada?"
                            :true-value="1"
                            :false-value="0"
                          />
                        </div>
                      </div>
                      <div>
                        <SwitchDefault
                          id="sw-edit-estado"
                          v-model="formData.alerEstado"
                          label="¿Alerta activa?"
                          :true-value="1"
                          :false-value="0"
                        />
                      </div>
                    </div>

                    <div class="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                      <ButtonDefault @click="cancelEdit">Cancelar</ButtonDefault>
                      <ButtonDefault
                        color="blue"
                        :is-loading="isSaving"
                        @click="handleSave"
                      >
                        Actualizar Alerta
                      </ButtonDefault>
                    </div>
                  </div>
              </Transition>
                </div>
                </TransitionGroup>
              </section>
            </div>

            <div v-if="selectedAlertas.length === 0" class="flex flex-col items-center justify-center py-16 opacity-30">
              <InformationCircleIcon class="w-12 h-12 mb-2" />
              <p class="text-sm font-bold uppercase tracking-widest">No hay alertas en esta sección</p>
            </div>
          </div>
        </div>

        <!-- SIDE DRAWER FOR CREATION -->
        <Transition
          enter-active-class="transition-all duration-500 ease-in-out"
          enter-from-class="translate-x-full"
          enter-to-class="translate-x-0"
          leave-active-class="transition-all duration-400 ease-in-out"
          leave-from-class="translate-x-0"
          leave-to-class="translate-x-full"
        >
          <div v-if="isDrawerOpen" class="absolute inset-y-0 right-0 w-full sm:w-[450px] bg-white dark:bg-gray-800 shadow-2xl border-l border-gray-200 dark:border-gray-700 z-50 overflow-y-auto">
            <div class="p-6 h-full flex flex-col">
              <div class="flex items-center justify-between mb-8 pb-4 border-b border-gray-100 dark:border-gray-700">
                <div class="flex items-center gap-3">
                  <div class="p-2 bg-blue-50 dark:bg-blue-900/40 rounded-xl text-blue-600 dark:text-blue-400">
                    <PlusIcon class="w-6 h-6" />
                  </div>
                  <div>
                    <h3 class="text-base font-bold text-gray-800 dark:text-gray-100 uppercase tracking-tight">Nueva Alerta</h3>
                    <p class="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Completa el formulario</p>
                  </div>
                </div>
                <button class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-gray-600" @click="toggleDrawer">
                  <XMarkIcon class="w-6 h-6" />
                </button>
              </div>
              <div class="flex-1 space-y-4 pt-2">
                <div class="space-y-4">
                  <!-- Categoría -->
                  <div>
                    <SelectDefault
                      v-model="formData.alerTablaTipoAlerta"
                      :options="categoriasFiltradas"
                      label="Selecciona una categoría"
                      :map-options="(item: any) => ({ optionText: item.comaNombrelargo, optionValue: item.comaId })"
                      :error="showError('formData.alerTablaTipoAlerta')"
                      @change="handleCategoryChange"
                    />
                  </div>

                  <!-- Tipo de alerta (solo cuando NO es TIPOS DE RUTA) -->
                  <div v-if="!isTipoRuta">
                    <SelectSearch
                      id="drawer_alert_select"
                      v-model:value="formData.alerTipoAlerta"
                      v-model:search="searchAlerta"
                      label="Selecciona un tipo de alerta"
                      :list-group="alertasGrouped || {}"
                      :is-group="true"
                      :readonly="!formData.alerTablaTipoAlerta"
                      :error="showError('formData.alerTipoAlerta')"
                      :options="{ fieldFilter: { name: 'comaNombrelargo', mapOptions: (item: any) => item.comaNombrelargo }, mapOptions: (item: any) => ({ optionText: item.comaNombrelargo, optionValue: item.comaId }) }"
                    />
                  </div>

                  <!-- Servicio (se usa para ambos cuando es TIPOS DE RUTA) -->
                  <div>
                    <SelectDefault
                      id="drawer_servicio_select"
                      v-model="formData.coseId"
                      label="Selecciona un servicio"
                      :options="serviciosList"
                      :map-options="(item: any) => ({ optionText: item.coseNombre, optionValue: item.coseId })"
                      :readonly="!formData.alerTablaTipoAlerta"
                      :error="showError('formData.coseId')"
                      placeholder="Seleccione un servicio"
                    />
                  </div>

                  <!-- Descripción -->
                  <div>
                    <TextareaDefault
                      v-model="formData.alerDetalle"
                      :maxlength="500"
                      label="Descripción de la alerta"
                      :show-character-count="true"
                      placeholder="Escribe los detalles aquí..."
                    />
                  </div>

                  <!-- Fecha y Privacidad -->
                  <div class="grid grid-cols-2 gap-4 items-end pt-2">
                    <div class="grid grid-cols-1 gap-1">
                      <DatePickerDefault v-model="formData.alerFecha" label="Fecha de vencimiento" :clearable="true" />
                    </div>
                    <div v-if="validatePermiso(['registrarAlertaPrivadaPaciente'])" class="pb-2 flex flex-col gap-2">
                      <SwitchDefault id="drawer-sw-priv" v-model="formData.alerIsPrivada" label="¿Es privada?" :true-value="1" :false-value="0" />
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-3">
                <ButtonDefault
                  @click="toggleDrawer"
                >
                  Cancelar
                </ButtonDefault>
                <ButtonDefault
                  color="blue"
                  :is-loading="isSaving"
                  @click="handleSave"
                >
                  {{ isEditing ? 'Actualizar' : 'Guardar' }} Alerta
                </ButtonDefault>
              </div>
            </div>
          </div>
        </Transition>

        <!-- Overlay for Drawer -->
        <Transition
          enter-active-class="transition-opacity duration-500 ease-linear"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition-opacity duration-300 ease-linear"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div v-if="isDrawerOpen" class="absolute inset-0 bg-gray-900/20 backdrop-blur-[1px] z-40" @click="toggleDrawer"></div>
        </Transition>

      </div>
    </template>

    <template #modal-footer>
      <div class="hidden"></div> <!-- Ocultamos footer default del modal ya que tenemos acciones internas -->
    </template>
  </ModalDefault>

  <RegistrarMotivoModal
    id="modal-motivo-estado-alerta"
    :title-component="esCierre ? 'Cerrar alerta' : 'Activar alerta'"
    :warning-message="esCierre
      ? 'Al cerrar la alerta se registrará el motivo de cierre.'
      : 'Al activar la alerta se registrará el motivo de apertura.'"
    :confirmation-title="esCierre
      ? '¿Confirmas cerrar esta alerta?'
      : '¿Confirmas activar esta alerta?'"
    :callback-fnc="guardarCambioEstado"
  />
</template>

<script setup lang="ts">
import { toRef, ref, computed } from 'vue'
import { useAlerts, groupAlertasForDisplay } from '../../composables/useAlerts'
import { openModal, validatePermiso } from '@/modules/Core/usings/generales'
import type { IAlertAlertas } from '@/modules/Asistencial/interfaces'
import type { IConfServicios } from '@/modules/Contratacion/interfaces'
import ButtonGroup from '@/modules/Core/components/general/ButtonGroup.vue'
import type { IButton } from '@/modules/Core/interfaces'
import BtnCerrarAlerta from '@/modules/DemandaInducida/Components/BtnCerrarAlerta.vue'
import RegistrarMotivoModal from '@/modules/Core/components/general/RegistrarMotivoModal.vue'
import { servicioService } from '@/services'

import {
  PlusIcon,
  XMarkIcon,
  XCircleIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  PencilIcon,
  CalendarIcon,
  ClockIcon
} from '@heroicons/vue/24/outline'
import { formatearFechaEnEspanol } from '@/modules/Core/usings/generales'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

interface Props {
  coppId: number
  alertas: IAlertAlertas[]
}

interface Emits {
  (e: 'alerta-creada'): void
  (e: 'update'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>() as (event: string, payload?: unknown) => void

// Usar el composable con todas las funcionalidades
const {
  isSaving,
  isEditing,
  formData,
  searchAlerta,
  alertasGrouped,
  categoriasFiltradas,
  saveAlerta,
  handleEdit: originalHandleEdit,
  resetForm,
  deleteAlert,
  handleCategoryChange,
  getUnifiedStyle,
  getAlertNombre,
  showError,
  validateForm,
  cambioEstado,
  initialize,
  setupWatchers,
  isTipoRuta,
  reloadAlertas
} = useAlerts({
  coppId: toRef(props, 'coppId'),
  emit
})

// Estado para servicios
const serviciosList = ref<IConfServicios[]>([])
const isDrawerOpen = ref(false)
const editingId = ref<number | null>(null)
const esCierre = computed(() => Number(formData.value.alerEstado) === 0)
const alertGroups = computed(() => groupAlertasForDisplay(props.alertas))
const selectedAlertas = computed(() => [...alertGroups.value.active, ...alertGroups.value.inactive])
const alertSections = computed(() => [
  { key: 'active', title: 'Alertas activas', alerts: alertGroups.value.active },
  { key: 'inactive', title: 'Alertas cerradas', alerts: alertGroups.value.inactive }
].filter(section => section.alerts.length > 0))
const expiredIds = computed(() => alertGroups.value.expiredIds)

// Cargar servicios cuando se abre el modal
const loadServicios = async () => {
  try {
    const { data } = await servicioService.search({ offset: 0, limit: 1000 })
    serviciosList.value = data.body.rows || []
  } catch (error) {
    console.error('Error cargando servicios:', error)
  }
}

const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value
  if (!isDrawerOpen.value) {
    resetForm()
  }
}

const handleNewAlert = () => {
  editingId.value = null
  resetForm()
  isDrawerOpen.value = true
}

const handleEdit = (alert: IAlertAlertas) => {
  editingId.value = alert.alerId || null
  originalHandleEdit(alert)
}

const getAlertActions = (alert: IAlertAlertas): IButton<unknown>[] => [
  {
    label: 'Editar alerta', click: () => handleEdit(alert), icon: 'PencilSquareIcon',
    class: 'size-8 rounded-full text-gray-500 hover:bg-blue-100 hover:text-blue-800 focus:ring-2 focus:ring-blue-500',
    show: () => validatePermiso(['editarAlertaPrivadaPaciente', 'editarAlertaPaciente']), description: '', onlyTooltip: false, color: 'blue', expanded: () => false
  },
  {
    label: 'Eliminar alerta', click: () => deleteAlert(alert), icon: 'TrashIcon',
    class: 'size-8 rounded-full text-gray-500 hover:bg-red-100 hover:text-red-800 focus:ring-2 focus:ring-red-500',
    show: () => validatePermiso(['eliminarAlertaPaciente']), description: '', onlyTooltip: false, color: 'red', expanded: () => false
  }
]

const canManageAlert = () =>
  validatePermiso(['editarAlertaPrivadaPaciente', 'editarAlertaPaciente', 'eliminarAlertaPaciente'])

const cancelEdit = () => {
  editingId.value = null
  resetForm()
}

const handleSave = async () => {
  if (!validateForm()) {
    window.notificacion.notificacion('failure', 'Por favor complete todos los campos requeridos')
    return
  }
  if (cambioEstado.value) {
    openModal('modal-motivo-estado-alerta')
    return
  }
  const success = await saveAlerta()
  if (success) {
    isDrawerOpen.value = false
    editingId.value = null
  }
}

const guardarCambioEstado = async (motivo: string) => {
  window.notificacion.loading().circle('Actualizando alerta...')
  try {
    const success = await saveAlerta(motivo)
    if (success) {
      isDrawerOpen.value = false
      editingId.value = null
    }
    return success
  } finally {
    window.notificacion.loading().remove()
  }
}

const handleClose = async () => {
  resetForm()
  isDrawerOpen.value = false
  editingId.value = null
}

// Logic for Redesigned Card (Copying from AlertsFull for internal use)
const getAlertCardBackground = (alert: IAlertAlertas) => {
  if (!alert.alerId) return 'bg-gray-50 dark:bg-gray-800'

  const isExpired = expiredIds.value.has(alert.alerId)
  if (isExpired) return 'bg-red-50 dark:bg-red-900/20'
  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.background || 'bg-gray-50 dark:bg-gray-800'
}

const getAlertCardBorder = (alert: IAlertAlertas) => {
  if (!alert.alerId) return 'border-gray-200 dark:border-gray-700'

  const isExpired = expiredIds.value.has(alert.alerId)
  if (isExpired) return 'border-red-200 dark:border-red-800'
  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.border || 'border-gray-200 dark:border-gray-700'
}

const getAlertTitleClass = (alert: IAlertAlertas) => {
  if (!alert.alerId) return 'text-gray-700 dark:text-gray-400'

  const isExpired = expiredIds.value.has(alert.alerId)
  if (isExpired) return 'text-red-700 dark:text-red-400'
  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.titleColor
}

const getAlertIconClasses = (alert: IAlertAlertas) => {
  if (!alert.alerId) return 'bg-gray-100 text-gray-500 border-gray-200'

  const isExpired = expiredIds.value.has(alert.alerId)
  if (isExpired) return 'bg-red-100 text-red-600 border-red-200 dark:bg-red-900/40 dark:border-red-800'
  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.iconClass || 'bg-gray-100 text-gray-500 border-gray-200'
}

const getAlertIcon = (alert: IAlertAlertas) => {
  if (!alert.alerId) return InformationCircleIcon

  const isExpired = expiredIds.value.has(alert.alerId)
  if (isExpired) return XCircleIcon
  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.icon || InformationCircleIcon
}

const getAlertTitle = (alert: IAlertAlertas) => {
  const style = getUnifiedStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string)
  return style.title
}

const handleAlertaCerrada = async () => {
  await reloadAlertas()
  emit('update')
}

initialize()
loadServicios()
setupWatchers()

</script>
